import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Select } from "@cofano/cck";
import { Tooltip } from "@mui/material";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoatOutlined";

import { gql } from "__generated__/gql";
import { Ships_AvailableShipSelectQuery, Ships_AvailableShipSelectQueryVariables } from "__generated__/graphql";
import { ChildPropsFromPagination } from "types/commons";
import { BargeType } from "__generated__/enums";

export type AvailableShipOption = ChildPropsFromPagination<Ships_AvailableShipSelectQuery, "availableShips">;

const getLabel = (option: AvailableShipOption) => `${option.ship.name} ${option.ship.eni ? `(${option.ship.eni})` : ""}`;

type AvailableShipSelectProps = {
	value: AvailableShipOption | null | undefined;
	onChange: (value: AvailableShipOption | undefined) => void;
	shipId?: number | undefined;
	availabilityProperties: {
		capacityTon: number;
		capacityM3: number;
		window: {
			from: number | null | undefined;
			to: number | null | undefined;
		};
	};
	disableClear?: boolean;
};

const AvailableShipSelect = ({ value, onChange, shipId, availabilityProperties, disableClear }: AvailableShipSelectProps) => {

	const client = useApolloClient();

	const onFetchOptions = async(params: string, page: number) => {

		try  {

			const result = await client.query<Ships_AvailableShipSelectQuery, Ships_AvailableShipSelectQueryVariables>({
				query: query,
				variables: {
					search: {
						name: params,
						ships: shipId ? [ shipId ] : undefined,
						availabilityProperties: availabilityProperties,
						excludeBargeTypes: [ BargeType.PusherTug ],
					},
					pagination: {
						size: 10,
						page: page - 1,
					},
				},
			});

			return {
				options: result.data.availableShips.content as AvailableShipOption[],
				totalResults: result.data.availableShips.totalElements,
			};

		} catch (response) {
			return {
				options: [],
				totalResults: 0,
			};
		}
	};

	return (
		<Select
			value={value}
			onChange={(option: AvailableShipOption | null) => onChange(option || undefined)}
			fetchOptions={onFetchOptions}
			paginated
			groupBy={(option: AvailableShipOption) => option.isAvailable ? intl.formatMessage({ id: "SHIP_AVAILABLE" }) : intl.formatMessage({ id: "SHIP_NOT_AVAILABLE" })}
			typeToSearchLabel={intl.formatMessage({ id: "TYPE_TO_SEARCH" })}
			loadingLabel={intl.formatMessage({ id: "LOADING_DATA" })}
			getOptionLabel={getLabel}
			renderOption={(props, option: AvailableShipOption) => (
				<li {...props}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
						<span>{getLabel(option)}</span>
						{option.hasActiveTimeCharter && (
							<Tooltip title={intl.formatMessage({ id: "SHIP_ACTIVE_TIME_CHARTER" })}>
								<DirectionsBoatIcon fontSize="small" />
							</Tooltip>
						)}
					</div>
				</li>
			)}
			clearable={disableClear}
			popoverHeight={300}
		/>
	);
};

const query = gql(`#graphql
query ships_availableShipSelect($pagination: Pagination!, $search: ShipSearch) {
	availableShips(pagination: $pagination, search: $search) {
		totalElements
		content {
			isAvailable
			hasActiveTimeCharter
			ship {
				id
				name
				eni
				mmsi
				fuelType
				defaultCharterSubtype
				operator {
					id
					name
					applicationRelation
				}
				owner {
					id
					name
					applicationRelation
				}
				agents {
					location {
						id
						description
					}
					agent {
						id
						name
					}
				}
			}
		}
	}
}
`);

export default AvailableShipSelect;
